import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ApiAdminService } from "app/core/services/api-admin.service";
import { CacheService } from "app/core/services/cache.service";
import { buildParams } from "app/shared/helpers/helpers";
import { Account } from "app/shared/models/account";
import { UserModule } from "app/shared/models/modules-names";
import { PaginatedData } from "app/shared/models/paginated-data.interface";
import { environment } from "environments/environment";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";

export const SELECTED_ACCOUNT_KEY: string = "selectedAccount";
const LAST_USED_ACCOUNT_KEY: string = "last-used-account-key";

@Injectable({
  providedIn: "root",
})
export class AccountService {
  private selectedAccountSource: BehaviorSubject<Account | null> =
    new BehaviorSubject<Account | null>(
      CacheService.getCached(SELECTED_ACCOUNT_KEY, null),
    );
  private selectedAccountModulesSource: BehaviorSubject<UserModule[]> =
    new BehaviorSubject<UserModule[]>([]);

  public selectedAccount$: Observable<Account | null> =
    this.selectedAccountSource.asObservable();

  public selectedAccountModules$: Observable<UserModule[]> =
    this.selectedAccountModulesSource.asObservable();

  constructor(
    private http: HttpClient,
    private apiAdminService: ApiAdminService,
  ) {
    if (this.selectedAccount) {
      this.emitSelectedAccountModules();
    }
  }

  set selectedAccount(account: Account | null) {
    this.selectedAccountSource.next(account);
    CacheService.store(SELECTED_ACCOUNT_KEY, account);
    this.emitSelectedAccountModules();
  }

  get selectedAccount(): Account | null {
    return this.selectedAccountSource.getValue();
  }

  get isKAccountSelected(): boolean {
    const type: any =
      this.selectedAccount &&
      this.selectedAccount.externalIdentifier!.charAt(0).toLowerCase();
    return type === "k";
  }

  public getAllAccounts(
    search: string = "",
    page: number = 1,
    perPage: number = 25,
    parentAccountId: string = "",
  ): Observable<PaginatedData<Account>> {
    const params: string = buildParams(
      search,
      page,
      perPage,
      "name",
      "nconnect_voice",
    );

    if (parentAccountId) {
      parentAccountId = `/${parentAccountId}`;
    }

    const options: any = { observe: "response" };

    return this.http
      .get(
        `${environment.API_URL}/admin/accounts${parentAccountId}?${params}`,
        options,
      )
      .pipe(
        map((response: any) => ({
          data: response.body,
          paginationHasMoreData:
            +response.headers.get("x-pagination-current-page") <
            +response.headers.get("x-pagination-page-count"),
        })),
        map(AccountService.mapResponseWithMultipleItems),
      );
  }

  public getAccount(accountId: string): Observable<Account> {
    return this.http
      .get(`${environment.API_URL}/admin/account/${accountId}`)
      .pipe(map(AccountService.mapResponseWithSingleItem));
  }

  public getAccountById(accountId: string): Observable<Account> {
    return this.http
      .get(`${environment.API_URL}/admin/accounts?search=${accountId}`)
      .pipe(
        map((account) => account[0]),
        map(AccountService.mapResponseWithSingleItem),
      );
  }

  public getParentAccounts(
    accountId: string,
  ): Observable<Account[] | undefined> {
    return this.getAccount(accountId).pipe(
      map((account: Account) => account.parentAccounts),
    );
  }

  public getParentAccount(accountId: string): Observable<Account | undefined> {
    return this.getParentAccounts(accountId).pipe(
      map(
        (parentAccounts: Account[] | undefined) =>
          parentAccounts && parentAccounts[0],
      ),
    );
  }

  /**
   * Get all the modules for a specific account - needed for Nconnect Voice
   */
  public getSelectedAccountModules(): Observable<UserModule[]> {
    return this.apiAdminService.get(
      `${environment.API_URL}/user/info/modules/${
        this.selectedAccount!.accountId
      }`,
    );
  }

  public storeLastUsedAccount(account: Account, userId: string): void {
    if (account.externalIdentifier && userId)
      localStorage.setItem(
        `${LAST_USED_ACCOUNT_KEY}-${userId}`,
        account.externalIdentifier,
      );
  }

  public getStoredAccount(userId: string): string {
    return localStorage.getItem(`${LAST_USED_ACCOUNT_KEY}-${userId}`) || "";
  }

  /**
   * Get modules for selected account - used by Nconnect Voice only (currently)
   */
  private emitSelectedAccountModules(): void {
    this.getSelectedAccountModules().subscribe((modules: UserModule[]) => {
      this.selectedAccountModulesSource.next(modules);
    });
  }

  /**
   * Map plain object to Account
   */
  public static mapToAccount(object: Account): Account {
    const account: Account = new Account();
    account.fromJSON(object);
    return account;
  }

  public static mapResponseWithMultipleItems(res: any): any {
    return res
      ? { ...res, data: res.data.map(AccountService.mapToAccount) }
      : res;
  }

  public static mapResponseWithSingleItem(res: any): any | Account {
    return res ? AccountService.mapToAccount(res) : res;
  }
}
